import { Grid, useMediaQuery } from "@material-ui/core";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { InationalTransportPoint, IProductPick, ItemAtPoint } from "contexts/RequestQuoteContext";
import { Address, Product } from "generated/graphql";
import React, { useCallback, useState } from "react";
import Dialog from "ui/Dialog";
import Switch from "ui/Switch";
import { uuid } from "utils";
import { isFieldWithValue, isNormalInteger } from "../shared/utils";
import DescribeProduct from "./DescribeProduct";
import FromCatalog from "./FromCatalog";
import { useFormStyles } from "./style";

interface IDialogPickProducts {
  open: boolean;
  onClose: () => void;
  onValidateProduct?: (newProduct: Product, counter: number, deleted: boolean) => void;
  articlesTopick: ItemAtPoint[];
  onValidateFromList: (products: ItemAtPoint[]) => void;
  onValidateCatalogProduct: (stop: InationalTransportPoint, products: ItemAtPoint[]) => void;
  catalogCheckedProducts: ItemAtPoint[];
  catalogProducts: IProductPick[];
  isForDelivery?: boolean;
  catalogProductsToPick?: ItemAtPoint[];
  positionPoint?: number;
  point?: InationalTransportPoint;
  checkedProducts: ItemAtPoint[];
  addressForPick?:string;
}

function PickProducts({
  open,
  onClose,
  onValidateProduct,
  articlesTopick,
  addressForPick,
  onValidateFromList,
  onValidateCatalogProduct,
  catalogCheckedProducts,
  catalogProducts,
  isForDelivery,
  catalogProductsToPick,
  positionPoint,
  point,
  checkedProducts,
}: IDialogPickProducts) {
  const { text } = useText();
  const classes = useFormStyles();
  const [showCatalog, setShowCatalog] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<boolean[]>([]);
  const dispatchAlert = useAlertDispatch();

  const catalogList = () => {
    const prods: ItemAtPoint[] = [];
    catalogCheckedProducts?.forEach((toPick: ItemAtPoint) => {
      const product = catalogProducts?.find((prod) => prod?._id === toPick?.product?._id);
      if (product) {
        prods.push({
          product: toPick?.product,
          counter: toPick?.counter,
          remainder: toPick?.remainder || toPick?.counter,
        });
      }
    });

    return prods;
  };

  const [newProductsChecked, setNewProductsChecked] = useState(catalogList() || []);

  const productList = () => {
    const prods: IProductPick[] = [];
    articlesTopick?.forEach((toPick: ItemAtPoint) => {
      const product = catalogProducts?.find((prod) => prod?._id === toPick?.product?._id);
      if (!product) {
        prods.push({
          ...toPick?.product,
          volume: toPick?.counter,
        });
      }
    });

    return prods;
  };
  const [newProdList, setNewProdList] = useState<IProductPick[]>(productList());

  const addDescribedProducts = useCallback(() => {
    const newArticlesToPick: ItemAtPoint[] = [];
    newProdList.forEach((prod) => {
      const newProduct = prod;
      if (isFieldWithValue(prod?.description) && isNormalInteger(prod?.volume?.toString()) && prod?.weight) {
        if (!prod._id) {
          newProduct._id = uuid();
          newProduct.refPickupDelivery = newProduct._id;
        }
        const newItem: ItemAtPoint = {
          product: newProduct,
          counter: prod.volume,
          remainder: prod.volume,
        };
        if (newItem.counter !== null && newItem.counter > 0) {
          newArticlesToPick.push(newItem);
        }
      }
    });
    return newArticlesToPick;
  }, [newProdList]);

  const hasErrors = () => {
    let error = false;
    if (newProdList.length > 0) {
      newProdList?.forEach((prod, index) => {
        const e = [...errors];
        if (!isFieldWithValue(prod?.description) || !isNormalInteger(prod?.volume?.toString()) || !prod?.weight) {
          e[index] = true;
          error = true;
        } else {
          e[index] = false;
        }
        setErrors(e);
      });
    }
    return error;
  };

  const handleValidation = () => {
    setSubmitted(true);
    if (!hasErrors()) {
      const describedProducts = addDescribedProducts();
      onValidateCatalogProduct(point, [...newProductsChecked, ...describedProducts]);
      onClose();
    } else {
      dispatchAlert({
        severity: "warning",
        message: text.request.describe_product_error,
      });
    }
  };

  const handleSwitch = (checked: boolean) => {
    if (checked) {
      setShowCatalog(false);
    } else if (!hasErrors()) {
      setShowCatalog(true);
    } else {
      dispatchAlert({
        severity: "warning",
        message: text.all.describe_product_error,
      });
    }
  };

  const renderCatalog = () => (
    <FromCatalog
      onValidate={onValidateCatalogProduct}
      productsChecked={catalogCheckedProducts}
      products={catalogProducts}
      positionPoint={positionPoint}
      productsToPick={checkedProducts}
      onSubmit={submitted}
      close={onClose}
      newProductsChecked={newProductsChecked}
      setNewProductsChecked={setNewProductsChecked}
      isForDelivery
    />
  );

  const renderDescribeProduct = () => (
    <DescribeProduct
      onValidate={onValidateProduct}
      articlesTopick={articlesTopick}
      catalogProducts={catalogProducts}
      onValidateFromList={onValidateFromList}
      onSubmit={submitted}
      newProdList={newProdList}
      setNewProdList={setNewProdList}
      close={onClose}
      errors={errors}
      setErrors={setErrors}
      hasErrors={hasErrors}
    />
  );
  const isSmallScreen = useMediaQuery("xs");
  
  return (
    <Dialog
      title={!showCatalog ? `${text.all.product_to_pick} : ` : `${text.all.product_to_pick_at} : ` }
      address={addressForPick}
      withTitle="24%"
      open={open}
      handleClose={onClose}
      handleConfirm={handleValidation}
      confirmButtonTitle="OK"
      maxWidth="lg"
      height={isSmallScreen ? "600px" : "auto"}
      sizeContent="60%"
    >
      <Grid container spacing={3} className={classes.swith_container}>
        <Switch
          primary={{
            label: "Describe Product",
            checked: !showCatalog,
          }}
          secondary={{
            label: "From Catalog",
            checked: showCatalog,
          }}
          onChange={handleSwitch}
          customWidth="450"
        />
      </Grid>
      <Grid>{showCatalog ? renderCatalog() : renderDescribeProduct()}</Grid>
    </Dialog>
  );
}

export default PickProducts;
